/**
 * CallRecordingContext
 *
 * Global state management for call recording that persists across tab navigation.
 * Allows the floating recorder widget to stay active while the engineer
 * switches between tabs.
 *
 * WS2: Dual-quality recording — two parallel MediaRecorders from the same source:
 * - PSA stream: 64kbps mono Opus 48kHz (clear voice for Halo ticket attachment)
 * - Whisper stream: 24kbps mono Opus 16kHz (small file for transcription)
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { createSession, saveChunk, deleteSession, updateSessionDuration, getRecoverableSessions, purgeStaleRecordings, type RecoverableSession } from '@/lib/recording-store'
import { v4 as uuidv4 } from 'uuid'

export type AudioCaptureMode = 'microphone' | 'system' | 'twoway'

export type RecordingStatus =
  | 'idle'
  | 'recording'
  | 'uploading'
  | 'processing'
  | 'complete'
  | 'error'

interface CallRecordingState {
  isRecording: boolean
  isPaused: boolean
  duration: number
  audioLevel: number
  captureMode: AudioCaptureMode | null
  status: RecordingStatus
  error: string | null
  ticketId: number | null
  ticketSummary: string | null
  recordingId: string | null
  audioBlob: Blob | null
  whisperBlob: Blob | null
  whisperStreamAvailable: boolean
  hasMicrophonePermission: boolean | null
  isMicrophoneSupported: boolean
  isSystemAudioSupported: boolean
}

interface CallRecordingActions {
  startRecording: (mode: AudioCaptureMode, ticketId?: number, ticketSummary?: string, speakerDeviceId?: string) => Promise<void>
  stopRecording: () => Promise<Blob | null>
  pauseRecording: () => void
  resumeRecording: () => void
  cancelRecording: () => void
  extractPartialAudio: () => Promise<Blob | null>
  setRecordingId: (id: string | null) => void
  setStatus: (status: RecordingStatus) => void
  setError: (error: string | null) => void
  reset: () => void
  recoverableSession: RecoverableSession | null
  recoverSession: () => void
  dismissRecovery: () => void
}

interface CallRecordingContextType extends CallRecordingState, CallRecordingActions {}

const CallRecordingContext = createContext<CallRecordingContextType | null>(null)

const INITIAL_STATE: CallRecordingState = {
  isRecording: false,
  isPaused: false,
  duration: 0,
  audioLevel: 0,
  captureMode: null,
  status: 'idle',
  error: null,
  ticketId: null,
  ticketSummary: null,
  recordingId: null,
  audioBlob: null,
  whisperBlob: null,
  whisperStreamAvailable: false,
  hasMicrophonePermission: null,
  isMicrophoneSupported: typeof window !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
  isSystemAudioSupported: typeof window !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia,
}

interface CallRecordingProviderProps {
  children: ReactNode
}

export function CallRecordingProvider({ children }: CallRecordingProviderProps) {
  const [state, setState] = useState<CallRecordingState>(INITIAL_STATE)
  const [recoverableSession, setRecoverableSession] = useState<RecoverableSession | null>(null)

  // IndexedDB session tracking
  const idbSessionIdRef = useRef<string | null>(null)
  const psaChunkIndexRef = useRef(0)
  const whisperChunkIndexRef = useRef(0)

  // PSA recorder (64kbps, 48kHz, clear quality)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])

  // Whisper recorder (24kbps, 16kHz, transcription quality)
  const whisperRecorderRef = useRef<MediaRecorder | null>(null)
  const whisperChunksRef = useRef<Blob[]>([])
  const whisperContextRef = useRef<AudioContext | null>(null)
  const whisperDestRef = useRef<MediaStreamAudioDestinationNode | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const speakerStreamRef = useRef<MediaStream | null>(null) // Two-way: separate speaker stream
  const mixerContextRef = useRef<AudioContext | null>(null) // Two-way: mixer AudioContext
  const mixerDestRef = useRef<MediaStreamAudioDestinationNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (speakerStreamRef.current) {
      speakerStreamRef.current.getTracks().forEach((track) => track.stop())
      speakerStreamRef.current = null
    }
    if (mixerContextRef.current) {
      mixerContextRef.current.close()
      mixerContextRef.current = null
    }
    mixerDestRef.current = null
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    if (whisperContextRef.current) {
      whisperContextRef.current.close()
      whisperContextRef.current = null
    }
    whisperDestRef.current = null
    analyserRef.current = null
    mediaRecorderRef.current = null
    whisperRecorderRef.current = null
  }, [])

  useEffect(() => {
    return () => { cleanup() }
  }, [cleanup])

  // Check for recoverable sessions on mount (WS6)
  useEffect(() => {
    purgeStaleRecordings().catch(() => {})
    getRecoverableSessions().then(sessions => {
      if (sessions.length > 0) {
        console.log(`[Recording] Found ${sessions.length} recoverable session(s)`)
        setRecoverableSession(sessions[0]) // Show the most recent one
      }
    }).catch(() => {})
  }, [])

  const setupAudioAnalyser = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)

      audioContextRef.current = audioContext
      analyserRef.current = analyser

      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length
          setState(prev => ({ ...prev, audioLevel: average / 255 }))
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
        }
      }

      updateAudioLevel()
    } catch (err) {
      console.warn('Could not setup audio analyser:', err)
    }
  }, [])

  // Create whisper stream: 16kHz (or 48kHz fallback) AudioContext → MediaStreamDestination → MediaRecorder
  const setupWhisperRecorder = useCallback((sourceStream: MediaStream) => {
    try {
      // Try 16kHz first — optimal for Whisper (~5 MB/hr)
      // Falls back to default 48kHz if unsupported (~10 MB/hr, still 82% smaller than current)
      let whisperCtx: AudioContext
      try {
        whisperCtx = new AudioContext({ sampleRate: 16000 })
        console.log('[Recording] Whisper AudioContext: 16kHz')
      } catch {
        whisperCtx = new AudioContext()
        console.log(`[Recording] Whisper AudioContext: ${whisperCtx.sampleRate}Hz (16kHz not supported, using fallback)`)
      }

      const source = whisperCtx.createMediaStreamSource(sourceStream)
      const dest = whisperCtx.createMediaStreamDestination()
      source.connect(dest)

      whisperContextRef.current = whisperCtx
      whisperDestRef.current = dest

      // Opus at 24kbps mono — optimized for speech-to-text
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'

      const whisperRecorder = new MediaRecorder(dest.stream, {
        mimeType,
        audioBitsPerSecond: 24000,
      })

      whisperChunksRef.current = []

      whisperRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          whisperChunksRef.current.push(event.data)
          if (idbSessionIdRef.current) {
            saveChunk({ sessionId: idbSessionIdRef.current, stream: 'whisper', index: whisperChunkIndexRef.current++, data: event.data }).catch(() => {})
          }
        }
      }

      whisperRecorder.onerror = (event) => {
        console.error('[Recording] Whisper recorder error:', (event as any).error?.message)
        // Non-fatal: PSA stream continues, we just lose the whisper optimization
      }

      whisperRecorderRef.current = whisperRecorder
      setState(prev => ({ ...prev, whisperStreamAvailable: true }))
      return true
    } catch (err) {
      console.warn('[Recording] Could not setup whisper recorder, will use PSA for transcription:', err)
      setState(prev => ({ ...prev, whisperStreamAvailable: false }))
      return false
    }
  }, [])

  const startRecording = useCallback(async (
    mode: AudioCaptureMode,
    ticketId?: number,
    ticketSummary?: string,
    speakerDeviceId?: string,
  ): Promise<void> => {
    setState(prev => ({ ...prev, error: null, status: 'recording' }))
    audioChunksRef.current = []
    whisperChunksRef.current = []

    try {
      let stream: MediaStream

      if (mode === 'twoway') {
        // ── Two-way: merge mic + speaker into single stream ──────────
        if (!state.isMicrophoneSupported) {
          throw new Error('Microphone access is not supported in this browser')
        }

        // 1. Get mic stream
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
        })
        streamRef.current = micStream

        // 2. Get speaker stream — loopback device or getDisplayMedia fallback
        let speakerStream: MediaStream
        if (speakerDeviceId) {
          // True loopback device (Stereo Mix, What U Hear) — zero dialogs
          console.log('[Recording] Two-way via loopback device:', speakerDeviceId)
          speakerStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: speakerDeviceId },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
              channelCount: 1,
            },
          })
        } else {
          // getDisplayMedia with optimized constraints for simpler dialog
          console.log('[Recording] Two-way via getDisplayMedia (system audio capture)')

          // Check browser support — system audio capture is Chromium-only
          const isChromium = !!('userAgentData' in navigator)
            || /Chrome|Chromium|Edg/i.test(navigator.userAgent)
          if (!isChromium) {
            micStream.getTracks().forEach((track) => track.stop())
            throw new Error(
              'Two-way recording requires Chrome or Edge. '
              + 'Firefox and Safari do not support system audio capture.'
            )
          }

          const displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              displaySurface: 'monitor',  // Default picker to "Entire Screen"
            },
            audio: {
              suppressLocalAudioPlayback: false,  // Keep audio playing through speakers
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
            },
            // Progressive enhancement — Chrome 107+ constraints
            // These simplify the picker dialog
            systemAudio: 'include',              // Ensure system audio checkbox appears
            selfBrowserSurface: 'exclude',       // Hide confusing "This Tab" option
            surfaceSwitching: 'exclude',         // Prevent mid-recording surface switching
            monitorTypeSurfaces: 'include',      // Ensure screen option is available
          } as DisplayMediaStreamOptions)

          const audioTracks = displayStream.getAudioTracks()
          if (audioTracks.length === 0) {
            displayStream.getTracks().forEach((track) => track.stop())
            micStream.getTracks().forEach((track) => track.stop())
            throw new Error(
              'No audio captured. Please try again:\n'
              + '1. Select your entire screen (not a window or tab)\n'
              + '2. Check "Share system audio" at the bottom of the dialog\n'
              + '3. Click Share'
            )
          }

          displayStream.getVideoTracks().forEach((track) => track.stop())
          speakerStream = new MediaStream(audioTracks)
        }
        speakerStreamRef.current = speakerStream

        // 3. Mix mic + speaker into a single mono stream via AudioContext
        const mixerCtx = new AudioContext()
        const micSource = mixerCtx.createMediaStreamSource(micStream)
        const speakerSource = mixerCtx.createMediaStreamSource(speakerStream)
        const dest = mixerCtx.createMediaStreamDestination()

        // Gain nodes to balance mic vs speaker (both at 1.0 for now)
        const micGain = mixerCtx.createGain()
        micGain.gain.value = 1.0
        const speakerGain = mixerCtx.createGain()
        speakerGain.gain.value = 1.0

        micSource.connect(micGain).connect(dest)
        speakerSource.connect(speakerGain).connect(dest)

        mixerContextRef.current = mixerCtx
        mixerDestRef.current = dest

        stream = dest.stream
        setupAudioAnalyser(stream)
      } else if (mode === 'system') {
        if (!state.isSystemAudioSupported) {
          throw new Error('System audio capture is not supported in this browser')
        }

        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: 'monitor',
          },
          audio: {
            suppressLocalAudioPlayback: false,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
          systemAudio: 'include',
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'exclude',
          monitorTypeSurfaces: 'include',
        } as DisplayMediaStreamOptions)

        const audioTracks = displayStream.getAudioTracks()
        if (audioTracks.length === 0) {
          displayStream.getTracks().forEach((track) => track.stop())
          throw new Error(
            'No audio captured. Please try again and check "Share system audio" at the bottom of the dialog.'
          )
        }

        displayStream.getVideoTracks().forEach((track) => track.stop())
        stream = new MediaStream(audioTracks)
        streamRef.current = stream
        setupAudioAnalyser(stream)
      } else {
        // Microphone only
        if (!state.isMicrophoneSupported) {
          throw new Error('Microphone access is not supported in this browser')
        }

        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
        })
        streamRef.current = stream
        setupAudioAnalyser(stream)
      }

      // ── PSA recorder: 64kbps mono Opus — clear voice for ticket attachment ──
      const psaMimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: psaMimeType,
        audioBitsPerSecond: 64000,
      })

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          if (idbSessionIdRef.current) {
            saveChunk({ sessionId: idbSessionIdRef.current, stream: 'psa', index: psaChunkIndexRef.current++, data: event.data }).catch(() => {})
          }
        }
      }

      mediaRecorder.onerror = (event) => {
        const errorMessage = 'Recording error: ' + ((event as any).error?.message || 'Unknown error')
        setState(prev => ({
          ...prev,
          error: errorMessage,
          status: 'error',
          isRecording: false,
          isPaused: false,
        }))
        cleanup()
      }

      mediaRecorderRef.current = mediaRecorder

      // ── Whisper recorder: 24kbps mono Opus 16kHz — small for transcription ──
      setupWhisperRecorder(stream)

      // ── IndexedDB session (WS6) ──
      const sessionId = uuidv4()
      idbSessionIdRef.current = sessionId
      psaChunkIndexRef.current = 0
      whisperChunkIndexRef.current = 0
      createSession({
        id: sessionId,
        startedAt: Date.now(),
        ticketId: ticketId ?? null,
        ticketSummary: ticketSummary ?? null,
        captureMode: mode,
        psaMimeType: psaMimeType,
        whisperMimeType: whisperRecorderRef.current?.mimeType || null,
        durationSeconds: 0,
      }).catch(err => console.warn('[Recording] IDB session create failed:', err))

      // Start both recorders
      mediaRecorder.start(1000)
      if (whisperRecorderRef.current) {
        whisperRecorderRef.current.start(1000)
      }

      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        duration: 0,
        captureMode: mode,
        ticketId: ticketId ?? null,
        ticketSummary: ticketSummary ?? null,
        hasMicrophonePermission: true,
        status: 'recording',
      }))

      timerRef.current = setInterval(() => {
        setState(prev => {
          // Update IDB duration periodically (every 10s to reduce writes)
          if (idbSessionIdRef.current && (prev.duration + 1) % 10 === 0) {
            updateSessionDuration(idbSessionIdRef.current, prev.duration + 1).catch(() => {})
          }
          return { ...prev, duration: prev.duration + 1 }
        })
      }, 1000)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      let errorMessage = error.message

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setState(prev => ({ ...prev, hasMicrophonePermission: false }))
        errorMessage = 'Permission denied. Please allow access and try again.'
      }

      setState(prev => ({
        ...prev,
        error: errorMessage,
        status: 'error',
        isRecording: false,
      }))
      cleanup()
    }
  }, [state.isSystemAudioSupported, state.isMicrophoneSupported, cleanup, setupAudioAnalyser, setupWhisperRecorder])

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(null)
        return
      }

      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }

      // Stop whisper recorder first (non-blocking)
      let whisperBlob: Blob | null = null
      const whisperPromise = new Promise<void>((resolveWhisper) => {
        if (whisperRecorderRef.current && whisperRecorderRef.current.state !== 'inactive') {
          whisperRecorderRef.current.onstop = () => {
            const mimeType = whisperRecorderRef.current?.mimeType || 'audio/webm'
            whisperBlob = new Blob(whisperChunksRef.current, { type: mimeType })
            console.log(`[Recording] Whisper blob: ${(whisperBlob.size / 1024).toFixed(1)} KB`)
            resolveWhisper()
          }
          whisperRecorderRef.current.stop()
        } else {
          resolveWhisper()
        }
      })

      mediaRecorderRef.current.onstop = async () => {
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'
        const blob = new Blob(audioChunksRef.current, { type: mimeType })
        console.log(`[Recording] PSA blob: ${(blob.size / 1024).toFixed(1)} KB`)

        // Wait for whisper to finish
        await whisperPromise

        // Observability: log stream sizes at upload time
        const psaSizeKB = (blob.size / 1024).toFixed(1)
        if (whisperBlob) {
          const whisperSizeKB = (whisperBlob.size / 1024).toFixed(1)
          const ratio = (blob.size / whisperBlob.size).toFixed(1)
          console.log(`[Recording] Upload streams — PSA: ${psaSizeKB} KB, Whisper: ${whisperSizeKB} KB (${ratio}x smaller)`)
        } else {
          console.warn(`[Recording] Upload streams — PSA: ${psaSizeKB} KB, Whisper: NONE (whisper recorder was not available, PSA will be used for transcription)`)
        }

        cleanup()

        // Clean up IDB session — blobs are in memory now, upload will handle them
        if (idbSessionIdRef.current) {
          deleteSession(idbSessionIdRef.current).catch(() => {})
          idbSessionIdRef.current = null
        }

        setState(prev => ({
          ...prev,
          isRecording: false,
          isPaused: false,
          audioLevel: 0,
          audioBlob: blob,
          whisperBlob,
          status: 'uploading',
        }))

        resolve(blob)
      }

      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      } else {
        resolve(null)
      }
    })
  }, [cleanup])

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording && !state.isPaused) {
      mediaRecorderRef.current.pause()
      if (whisperRecorderRef.current && whisperRecorderRef.current.state === 'recording') {
        whisperRecorderRef.current.pause()
      }
      setState(prev => ({ ...prev, isPaused: true }))

      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [state.isRecording, state.isPaused])

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording && state.isPaused) {
      mediaRecorderRef.current.resume()
      if (whisperRecorderRef.current && whisperRecorderRef.current.state === 'paused') {
        whisperRecorderRef.current.resume()
      }
      setState(prev => ({ ...prev, isPaused: false }))

      timerRef.current = setInterval(() => {
        setState(prev => ({ ...prev, duration: prev.duration + 1 }))
      }, 1000)
    }
  }, [state.isRecording, state.isPaused])

  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (idbSessionIdRef.current) {
      deleteSession(idbSessionIdRef.current).catch(() => {})
      idbSessionIdRef.current = null
    }
    cleanup()
    setState(INITIAL_STATE)
  }, [cleanup])

  const extractPartialAudio = useCallback(async (): Promise<Blob | null> => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      return null
    }

    // Flush current chunk from the MediaRecorder
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current!
      const onData = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
        recorder.removeEventListener('dataavailable', onData)

        // Snapshot all chunks collected so far
        const mimeType = recorder.mimeType || 'audio/webm'
        const blob = new Blob([...audioChunksRef.current], { type: mimeType })
        resolve(blob)
      }

      recorder.addEventListener('dataavailable', onData)
      recorder.requestData()
    })
  }, [])

  const setRecordingId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, recordingId: id }))
  }, [])

  const setStatus = useCallback((status: RecordingStatus) => {
    setState(prev => ({ ...prev, status }))
  }, [])

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error, status: error ? 'error' : prev.status }))
  }, [])

  const reset = useCallback(() => {
    cleanup()
    setState(INITIAL_STATE)
  }, [cleanup])

  // WS6: Recover a session from IndexedDB
  const recoverSession = useCallback(() => {
    if (!recoverableSession) return
    const { session, psaBlob, whisperBlob } = recoverableSession

    console.log(`[Recording] Recovering session ${session.id}: PSA ${psaBlob ? (psaBlob.size / 1024).toFixed(1) + ' KB' : 'none'}, Whisper ${whisperBlob ? (whisperBlob.size / 1024).toFixed(1) + ' KB' : 'none'}`)

    setState(prev => ({
      ...prev,
      audioBlob: psaBlob,
      whisperBlob: whisperBlob,
      duration: session.durationSeconds,
      ticketId: session.ticketId,
      ticketSummary: session.ticketSummary,
      captureMode: session.captureMode as AudioCaptureMode,
      status: 'uploading',
    }))

    // Clean up IDB after recovery
    deleteSession(session.id).catch(() => {})
    setRecoverableSession(null)
  }, [recoverableSession])

  const dismissRecovery = useCallback(() => {
    if (recoverableSession) {
      deleteSession(recoverableSession.session.id).catch(() => {})
      setRecoverableSession(null)
    }
  }, [recoverableSession])

  const contextValue: CallRecordingContextType = {
    ...state,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    extractPartialAudio,
    setRecordingId,
    setStatus,
    setError,
    reset,
    recoverableSession,
    recoverSession,
    dismissRecovery,
  }

  return (
    <CallRecordingContext.Provider value={contextValue}>
      {children}
    </CallRecordingContext.Provider>
  )
}

export function useCallRecordingContext() {
  const context = useContext(CallRecordingContext)
  if (!context) {
    throw new Error('useCallRecordingContext must be used within a CallRecordingProvider')
  }
  return context
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
